import {
  ANNUAL_DISCOUNT_PCT as CATALOG_ANNUAL_DISCOUNT_PCT,
  isPlanId,
  meetsPlan as meetsCatalogPlan,
  PLAN_IDS,
  planLines,
  planName,
  PLANS,
  RESERVAS_FREE_BOOKINGS_PER_MONTH as CATALOG_FREE_BOOKINGS,
  RESERVAS_PER_BOOKING_CENTS as CATALOG_PER_BOOKING_CENTS,
  type PlanId,
} from '@dale/plans';

/**
 * The CRM's view of Dale's plans. Names, prices, pitches and what each plan
 * includes come from the plan catalog (@dale/plans), the same file the API
 * gates with and the website sells from; this module only adds the price
 * arithmetic the screens need.
 */
export type PlanTier = PlanId;

/** One line per plan: who it is for, in the owner's words. */
export const PLAN_PITCH: Readonly<Record<PlanTier, string>> = Object.fromEntries(
  PLAN_IDS.map((plan) => [plan, PLANS[plan].pitch]),
) as Record<PlanTier, string>;

/** Undefined reads as 'base', matching the server's default when `plan` is absent. */
export function meetsPlan(current: PlanTier | undefined, required: PlanTier): boolean {
  return meetsCatalogPlan(current, required);
}

export function planLabel(plan: PlanTier): string {
  return planName(plan);
}

/** Cheapest first: the order every plan list and picker uses. */
export const PLAN_ORDER: readonly PlanTier[] = PLAN_IDS;

/** List price per month, in cents, before any discount. */
export const PLAN_LIST_PRICE_CENTS: Readonly<Record<PlanTier, number>> = Object.fromEntries(
  PLAN_IDS.map((plan) => [plan, PLANS[plan].listPriceCents]),
) as Record<PlanTier, number>;

/** Independientes pay per booking instead of a monthly fee. */
export const RESERVAS_PER_BOOKING_CENTS = CATALOG_PER_BOOKING_CENTS;
/** Bookings from the app each month Independientes never pays for. */
export const RESERVAS_FREE_BOOKINGS_PER_MONTH = CATALOG_FREE_BOOKINGS;

/** Month by month, or the whole year up front. */
export type BillingCycle = 'monthly' | 'annual';

/** Whole percent off twelve months when the year is paid up front. Membresía reads the server's own figure instead. */
export const ANNUAL_DISCOUNT_PCT = CATALOG_ANNUAL_DISCOUNT_PCT;
const MONTHS_PER_YEAR = 12;
const WHOLE_PERCENT = 100;

/** Independientes pays per booking, so it has no monthly fee to prepay. */
export function planHasAnnualBilling(plan: PlanTier): boolean {
  return PLAN_LIST_PRICE_CENTS[plan] > 0;
}

/** The cycle a plan is actually billed on: Independientes is always monthly. */
export function effectiveCycle(plan: PlanTier, cycle: BillingCycle): BillingCycle {
  return planHasAnnualBilling(plan) ? cycle : 'monthly';
}

/** Twelve months of `monthlyPriceCents` less the annual discount, rounded to the cent. */
export function annualPriceCents(monthlyPriceCents: number, discountPct = ANNUAL_DISCOUNT_PCT): number {
  return Math.round((monthlyPriceCents * MONTHS_PER_YEAR * (WHOLE_PERCENT - discountPct)) / WHOLE_PERCENT);
}

/** What the year up front saves against twelve monthly payments. */
export function annualSavingsCents(monthlyPriceCents: number, discountPct = ANNUAL_DISCOUNT_PCT): number {
  return monthlyPriceCents * MONTHS_PER_YEAR - annualPriceCents(monthlyPriceCents, discountPct);
}

/** The year up front read per month ("$40 /mes" for Pro), rounded to the cent. */
export function annualMonthlyEquivalentCents(monthlyPriceCents: number, discountPct = ANNUAL_DISCOUNT_PCT): number {
  return Math.round(annualPriceCents(monthlyPriceCents, discountPct) / MONTHS_PER_YEAR);
}

/** Twelve monthly payments, the figure the yearly total is compared against. */
export function twelveMonthsCents(monthlyPriceCents: number): number {
  return monthlyPriceCents * MONTHS_PER_YEAR;
}

/** Where signup starts when no link says otherwise. */
export const SIGNUP_DEFAULT_PLAN: PlanTier = 'pro';
/** The Spanish words the marketing site puts in `?ciclo=`. */
const CYCLE_PARAM: ReadonlyMap<string, BillingCycle> = new Map([
  ['mensual', 'monthly'],
  ['anual', 'annual'],
]);

/** Reads `?plan=base|pro|max|ultra|reservas&ciclo=mensual|anual`; anything unknown keeps the default. */
export function readPlanChoice(search: string): { plan: PlanTier; cycle: BillingCycle } {
  const params = new URLSearchParams(search);
  const plan = params.get('plan')?.trim().toLowerCase() ?? '';
  const cycle = params.get('ciclo')?.trim().toLowerCase() ?? '';
  return {
    plan: isPlanTier(plan) ? plan : SIGNUP_DEFAULT_PLAN,
    // Signup opens on the year: the owner sees the monthly amount for 12 months and can switch to monthly.
    cycle: CYCLE_PARAM.get(cycle) ?? 'annual',
  };
}

/** What each plan adds over the one below it, in the owner's own words. */
export const PLAN_FEATURES: Readonly<Record<PlanTier, readonly string[]>> = Object.fromEntries(
  PLAN_IDS.map((plan) => [plan, PLANS[plan].adds]),
) as Record<PlanTier, readonly string[]>;

/**
 * What a plan leaves out, said before anyone picks it. Independientes is one
 * person working alone (a coach, a contractor): a business with a team or a
 * local starts at Base.
 */
export const PLAN_LIMIT: Readonly<Partial<Record<PlanTier, string>>> = Object.fromEntries(
  PLAN_IDS.flatMap((plan) => {
    const limit = PLANS[plan].limit;
    return limit ? [[plan, limit]] : [];
  }),
);

/** The lines a plan card lists: a paid plan opens with everything the plan below it has, then what it adds. */
export function planCardFeatures(plan: PlanTier): readonly string[] {
  return planLines(plan);
}

/** The share of the list price a negotiated price takes off, 0 to 0.9. */
export function discountFraction(plan: PlanTier, priceCents: number): number {
  const list = PLAN_LIST_PRICE_CENTS[plan];
  if (list <= 0) return 0;
  return Math.min(0.9, Math.max(0, 1 - priceCents / list));
}

/** The price a plan costs with the same negotiated discount carried over. */
export function priceWithDiscount(plan: PlanTier, fraction: number): number {
  return Math.round(PLAN_LIST_PRICE_CENTS[plan] * (1 - fraction));
}

export function isPlanTier(value: string): value is PlanTier {
  return isPlanId(value);
}
